import type { ColumnDefinition } from "@frui.ts/dataviews";
import React from "react";
import type { ViewComponent } from "@frui.ts/views/dist";
import { registerView } from "@frui.ts/views/dist";
import { observer } from "mobx-react-lite";
import ListView from "../../common/list";
import LustrationReportsViewModel from "viewModels/enforcement/lustrationReports/lustrationReportsViewModel";
import type LustrationReportResponse from "entities/lustrationReportResponse";
import LustrationReportsFilters from "./lustrationReportsFilters";
import { formatDateTime, formatLicensePlate } from "utils/localizations";
import ReportStatus from "entities/reportStatus";
import { busyIdentificator } from "utils/busyWatcher";
import Button from "@edaz/edazkit/Button/index";
import { IconType } from "@edaz/edazkit/Icon";

const LustrationReportsView: ViewComponent<LustrationReportsViewModel> = observer(({ vm }) => {
  const textLeftClassName = "text-left";

  const columns: ColumnDefinition<LustrationReportResponse>[] = [
    {
      title: vm.translateAttribute("reportSequence"),
      cellClassName: textLeftClassName,
      headerClassName: textLeftClassName,
      property: "reportSequence",
    },
    {
      title: vm.translateAttribute("vehicleRegistration"),
      cellClassName: textLeftClassName,
      headerClassName: textLeftClassName,
      property: "vehicleRegistration",
      valueFormatter: props => formatLicensePlate(props.value),
    },
    {
      title: vm.translateAttribute("eventOccurredAt"),
      cellClassName: textLeftClassName,
      headerClassName: textLeftClassName,
      property: "eventOccurredAt",
      valueFormatter: props => (props.value ? formatDateTime(new Date(props.value)) : ""),
    },
    {
      title: vm.translateAttribute("processedAt"),
      cellClassName: textLeftClassName,
      headerClassName: textLeftClassName,
      property: "processedAt",
      valueFormatter: props => (props.value ? formatDateTime(new Date(props.value)) : ""),
    },
    {
      title: vm.translateAttribute("status"),
      cellClassName: textLeftClassName,
      headerClassName: textLeftClassName,
      property: "status",
      valueFormatter: props => vm.translateStatus(props.value),
    },
  ];

  const actionButtons = (item: LustrationReportResponse) => {
    const isBusy = vm.busyWatcher.checkBusy(busyIdentificator([item]));

    return (
      <>
        {item.status === ReportStatus.Processed && (
          <Button
            variant="success"
            size="sm"
            className="rounded"
            onClick={() => vm.onDownload(item)}
            disabled={isBusy}
            iconLeft={isBusy ? IconType.Spinner : IconType.Download}>
            {vm.translate("downloadFile")}
          </Button>
        )}
      </>
    );
  };

  const headerButtons = vm.allowWrite ? (
    <Button className="rounded" size="sm" iconLeft={IconType.SignPlus} onClick={vm.onAddClick()}>
      {vm.translate("create")}
    </Button>
  ) : null;

  return (
    <ListView
      columns={columns}
      vm={vm}
      showDetailWithList={true}
      aboveTable={<LustrationReportsFilters vm={vm} />}
      actionButtons={actionButtons}
      headerButtons={headerButtons}
      forceHeaderButtons
    />
  );
});

registerView(LustrationReportsView, LustrationReportsViewModel);
